import { useState, useEffect, useCallback } from "react";
import { useAuth, hasPermission, getRoleLabel, getRoleColor } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "@/lib/api";
import { MapPin, Bell, Sparkles, Shield, LogOut, Building2, Edit2, Save, X, Lock, Check, Smartphone, Eye, EyeOff } from "lucide-react";
import "./Settings.css";

export default function Settings() {
  const { user, logout, trialStatus, updateStoreProfile, changePassword } = useAuth();
  const navigate = useNavigate();
  const canManageStore = hasPermission(user?.role, "settings:store");
  const canManageAI = hasPermission(user?.role, "settings:ai");

  const [editingStore, setEditingStore] = useState(false);
  const [storeForm, setStoreForm] = useState({
    storeName: "",
    firstName: "",
    lastName: "",
    phone: "",
    storeAddress: "",
    city: "",
    state: "",
    zip: "",
  });
  const [storeSaving, setStoreSaving] = useState(false);
  const [storeSuccess, setStoreSuccess] = useState(false);

  const startEditing = useCallback(async () => {
    if (!user?.storeId) return;
    try {
      const store = await apiRequest<any>(`/stores/${user.storeId}`);
      setStoreForm({
        storeName: store.storeName || user.storeLocation || "",
        firstName: store.firstName || user.name?.split(" ")[0] || "",
        lastName: store.lastName || user.name?.split(" ").slice(1).join(" ") || "",
        phone: store.phone || "",
        storeAddress: store.storeAddress || "",
        city: store.city || "",
        state: store.state || "",
        zip: store.zip || "",
      });
    } catch {
      setStoreForm({
        storeName: user.storeLocation || "",
        firstName: user.name?.split(" ")[0] || "",
        lastName: user.name?.split(" ").slice(1).join(" ") || "",
        phone: "",
        storeAddress: "",
        city: "",
        state: "",
        zip: "",
      });
    }
    setEditingStore(true);
  }, [user]);

  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const [storeError, setStoreError] = useState("");

  const [twilioConfig, setTwilioConfig] = useState({ twilioSid: "", twilioAuthToken: "", twilioPhone: "", smsEnabled: false });
  const [editingTwilio, setEditingTwilio] = useState(false);
  const [twilioSaving, setTwilioSaving] = useState(false);
  const [twilioSuccess, setTwilioSuccess] = useState(false);
  const [twilioError, setTwilioError] = useState("");
  const [showAuthToken, setShowAuthToken] = useState(false);

  const fetchTwilioConfig = useCallback(async () => {
    if (!user?.storeId) return;
    try {
      const data = await apiRequest<any>(`/sms/config?storeId=${user.storeId}`);
      setTwilioConfig({
        twilioSid: data.twilioSid || "",
        twilioAuthToken: "",
        twilioPhone: data.twilioPhone || "",
        smsEnabled: data.smsEnabled || false,
      });
    } catch {}
  }, [user?.storeId]);

  useEffect(() => { fetchTwilioConfig(); }, [fetchTwilioConfig]);

  const handleSaveTwilio = async () => {
    if (!user?.storeId) return;
    setTwilioSaving(true);
    setTwilioError("");
    try {
      const payload: any = {
        storeId: user.storeId,
        twilioSid: twilioConfig.twilioSid,
        twilioPhone: twilioConfig.twilioPhone,
        smsEnabled: twilioConfig.smsEnabled,
      };
      if (twilioConfig.twilioAuthToken) {
        payload.twilioAuthToken = twilioConfig.twilioAuthToken;
      }
      await apiRequest("/sms/config", { method: "PUT", body: JSON.stringify(payload) });
      setTwilioSuccess(true);
      setEditingTwilio(false);
      setTimeout(() => setTwilioSuccess(false), 3000);
      fetchTwilioConfig();
    } catch (err: any) {
      setTwilioError(err.message || "Failed to save Twilio config");
    }
    setTwilioSaving(false);
  };

  const handleSaveStore = async () => {
    if (!user?.storeId) return;
    setStoreSaving(true);
    setStoreError("");
    const result = await updateStoreProfile(user.storeId, storeForm);
    setStoreSaving(false);
    if (result.success) {
      setStoreSuccess(true);
      setEditingStore(false);
      setTimeout(() => setStoreSuccess(false), 3000);
    } else {
      setStoreError(result.error || "Failed to save changes");
    }
  };

  const handleChangePassword = async () => {
    if (!user?.storeId) return;
    setPasswordError("");
    if (passwordForm.newPassword.length < 6) {
      setPasswordError("Password must be at least 6 characters");
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError("Passwords don't match");
      return;
    }
    setPasswordSaving(true);
    const result = await changePassword(user.storeId, passwordForm.currentPassword, passwordForm.newPassword);
    setPasswordSaving(false);
    if (result.success) {
      setPasswordSuccess(true);
      setChangingPassword(false);
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
      setTimeout(() => setPasswordSuccess(false), 3000);
    } else {
      setPasswordError(result.error || "Failed to change password");
    }
  };

  const initials = user?.name?.split(" ").map((n) => n[0]).join("").toUpperCase() || "?";
  const roleLabel = user?.role ? getRoleLabel(user.role) : "Sales";
  const roleColor = user?.role ? getRoleColor(user.role) : "var(--primary)";

  return (
    <div className="page" style={{ maxWidth: 700 }}>
      <div className="page-header">
        <h1>Settings</h1>
      </div>

      <div className="card settings-profile">
        <div className="avatar-xl">{initials}</div>
        <div>
          <h2>{user?.name}</h2>
          <p>{user?.email}</p>
          <span className="role-badge" style={{ background: `${roleColor}15`, color: roleColor, border: `1px solid ${roleColor}30` }}>
            {roleLabel}
          </span>
        </div>
      </div>

      <div className="settings-sections">
        <div className="card settings-section">
          <h3><MapPin size={16} /> Store</h3>
          <div className="settings-item">
            <span className="settings-label">Location</span>
            <span>{user?.storeLocation}</span>
          </div>
        </div>

        <div className="card settings-section">
          <h3><Bell size={16} /> Preferences</h3>
          <div className="settings-item">
            <span className="settings-label">Notifications</span>
            <span>Enabled</span>
          </div>
          <div className="settings-item">
            <span className="settings-label">Follow-up reminders</span>
            <span>Daily</span>
          </div>
        </div>

        {canManageAI && (
          <div className="card settings-section">
            <h3><Sparkles size={16} /> AI Assistant</h3>
            <div className="settings-item">
              <span className="settings-label">Furniture Fred</span>
              <span>Active</span>
            </div>
            <div className="settings-item">
              <span className="settings-label">AI Model</span>
              <span>GPT-5.2</span>
            </div>
          </div>
        )}

        {canManageStore && (
          <div className="card settings-section">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3><Building2 size={16} /> Store Configuration</h3>
              {!editingStore && (
                <button className="btn btn-secondary btn-sm" onClick={startEditing}>
                  <Edit2 size={14} /> Edit
                </button>
              )}
            </div>

            {storeSuccess && (
              <div style={{ background: "#ECFDF5", border: "1px solid #10B981", borderRadius: 6, padding: "8px 12px", marginBottom: 12, display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#065F46" }}>
                <Check size={14} /> Store information updated successfully
              </div>
            )}

            {editingStore ? (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
                <div className="form-group"><label>Store Name</label><input value={storeForm.storeName} onChange={(e) => setStoreForm({ ...storeForm, storeName: e.target.value })} /></div>
                <div className="form-group"><label>Phone</label><input value={storeForm.phone} onChange={(e) => setStoreForm({ ...storeForm, phone: e.target.value })} /></div>
                <div className="form-group"><label>First Name</label><input value={storeForm.firstName} onChange={(e) => setStoreForm({ ...storeForm, firstName: e.target.value })} /></div>
                <div className="form-group"><label>Last Name</label><input value={storeForm.lastName} onChange={(e) => setStoreForm({ ...storeForm, lastName: e.target.value })} /></div>
                <div className="form-group" style={{ gridColumn: "1 / -1" }}><label>Address</label><input value={storeForm.storeAddress} onChange={(e) => setStoreForm({ ...storeForm, storeAddress: e.target.value })} /></div>
                <div className="form-group"><label>City</label><input value={storeForm.city} onChange={(e) => setStoreForm({ ...storeForm, city: e.target.value })} /></div>
                <div className="form-group">
                  <div style={{ display: "flex", gap: 8 }}>
                    <div className="form-group" style={{ flex: 1 }}><label>State</label><input value={storeForm.state} onChange={(e) => setStoreForm({ ...storeForm, state: e.target.value })} /></div>
                    <div className="form-group" style={{ flex: 1 }}><label>Zip</label><input value={storeForm.zip} onChange={(e) => setStoreForm({ ...storeForm, zip: e.target.value })} /></div>
                  </div>
                </div>
                {storeError && <div style={{ gridColumn: "1 / -1", color: "#EF4444", fontSize: 13, marginTop: 4 }}>{storeError}</div>}
                <div style={{ gridColumn: "1 / -1", display: "flex", gap: 8, marginTop: 4 }}>
                  <button className="btn btn-primary btn-sm" onClick={handleSaveStore} disabled={storeSaving}>
                    <Save size={14} /> {storeSaving ? "Saving..." : "Save Changes"}
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={() => { setEditingStore(false); setStoreError(""); }}>
                    <X size={14} /> Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="settings-item">
                  <span className="settings-label">Business Name</span>
                  <span>{user?.storeLocation || "My Store"}</span>
                </div>
                <div className="settings-item">
                  <span className="settings-label">Subscription</span>
                  <span className="plan-badge">
                    {trialStatus.isOnTrial ? `Trial (${trialStatus.daysLeft}d left)` : trialStatus.isExpired ? "Expired" : "Active"}
                  </span>
                </div>
              </>
            )}
          </div>
        )}

        {canManageStore && user?.storeId && (
          <div className="card settings-section">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3><Lock size={16} /> Password</h3>
              {!changingPassword && (
                <button className="btn btn-secondary btn-sm" onClick={() => setChangingPassword(true)}>
                  Change Password
                </button>
              )}
            </div>

            {passwordSuccess && (
              <div style={{ background: "#ECFDF5", border: "1px solid #10B981", borderRadius: 6, padding: "8px 12px", marginBottom: 12, display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#065F46" }}>
                <Check size={14} /> Password changed successfully
              </div>
            )}

            {changingPassword && (
              <div style={{ marginTop: 12 }}>
                <div className="form-group"><label>Current Password</label><input type="password" value={passwordForm.currentPassword} onChange={(e) => { setPasswordForm({ ...passwordForm, currentPassword: e.target.value }); setPasswordError(""); }} /></div>
                <div className="form-group"><label>New Password</label><input type="password" value={passwordForm.newPassword} onChange={(e) => { setPasswordForm({ ...passwordForm, newPassword: e.target.value }); setPasswordError(""); }} placeholder="At least 6 characters" /></div>
                <div className="form-group"><label>Confirm New Password</label><input type="password" value={passwordForm.confirmPassword} onChange={(e) => { setPasswordForm({ ...passwordForm, confirmPassword: e.target.value }); setPasswordError(""); }} /></div>
                {passwordError && <p style={{ color: "#EF4444", fontSize: 13, marginBottom: 8 }}>{passwordError}</p>}
                <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                  <button className="btn btn-primary btn-sm" onClick={handleChangePassword} disabled={passwordSaving}>
                    {passwordSaving ? "Saving..." : "Update Password"}
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={() => { setChangingPassword(false); setPasswordError(""); }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {canManageStore && user?.storeId && (
          <div className="card settings-section">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3><Smartphone size={16} /> SMS Texting (Twilio)</h3>
              {!editingTwilio && (
                <button className="btn btn-secondary btn-sm" onClick={() => setEditingTwilio(true)}>
                  <Edit2 size={14} /> Configure
                </button>
              )}
            </div>
            <p style={{ fontSize: 12, color: "#94A3B8", marginBottom: 12 }}>
              Connect your Twilio account to send SMS from tablets without cell service. Your store pays Twilio directly for SMS usage.
            </p>

            {twilioSuccess && (
              <div style={{ background: "#ECFDF5", border: "1px solid #10B981", borderRadius: 6, padding: "8px 12px", marginBottom: 12, display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#065F46" }}>
                <Check size={14} /> Twilio configuration saved
              </div>
            )}

            {editingTwilio ? (
              <div style={{ marginTop: 8 }}>
                <div className="form-group">
                  <label>Account SID</label>
                  <input value={twilioConfig.twilioSid} onChange={(e) => setTwilioConfig({ ...twilioConfig, twilioSid: e.target.value })} placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" />
                </div>
                <div className="form-group">
                  <label>Auth Token</label>
                  <div style={{ display: "flex", gap: 6 }}>
                    <input type={showAuthToken ? "text" : "password"} value={twilioConfig.twilioAuthToken}
                      onChange={(e) => setTwilioConfig({ ...twilioConfig, twilioAuthToken: e.target.value })}
                      placeholder="Leave blank to keep existing token" style={{ flex: 1 }} />
                    <button className="btn btn-secondary btn-sm" onClick={() => setShowAuthToken(!showAuthToken)} style={{ padding: "6px 8px" }}>
                      {showAuthToken ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                </div>
                <div className="form-group">
                  <label>Twilio Phone Number</label>
                  <input value={twilioConfig.twilioPhone} onChange={(e) => setTwilioConfig({ ...twilioConfig, twilioPhone: e.target.value })} placeholder="+1XXXXXXXXXX" />
                </div>
                <div className="form-group">
                  <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                    <input type="checkbox" checked={twilioConfig.smsEnabled}
                      onChange={(e) => setTwilioConfig({ ...twilioConfig, smsEnabled: e.target.checked })}
                      style={{ width: 16, height: 16, accentColor: "#8B5CF6" }} />
                    Enable SMS Texting
                  </label>
                </div>
                {twilioError && <p style={{ color: "#EF4444", fontSize: 13, marginBottom: 8 }}>{twilioError}</p>}
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <button className="btn btn-primary btn-sm" onClick={handleSaveTwilio} disabled={twilioSaving}>
                    <Save size={14} /> {twilioSaving ? "Saving..." : "Save Twilio Config"}
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={() => { setEditingTwilio(false); setTwilioError(""); }}>
                    <X size={14} /> Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="settings-item">
                  <span className="settings-label">Status</span>
                  <span style={{ color: twilioConfig.smsEnabled ? "#10B981" : "#94A3B8", fontWeight: 600 }}>
                    {twilioConfig.smsEnabled ? "Active" : "Not Configured"}
                  </span>
                </div>
                {twilioConfig.twilioPhone && (
                  <div className="settings-item">
                    <span className="settings-label">Twilio Number</span>
                    <span>{twilioConfig.twilioPhone}</span>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <div className="card settings-section">
          <h3><Shield size={16} /> Account</h3>
          <div className="settings-item">
            <span className="settings-label">Role</span>
            <span style={{ color: roleColor, fontWeight: 600 }}>{roleLabel}</span>
          </div>
          <div className="settings-item">
            <span className="settings-label">User ID</span>
            <span>{user?.id}</span>
          </div>
          <button className="btn btn-danger" onClick={handleLogout} style={{ marginTop: 12 }}>
            <LogOut size={16} /> Log Out
          </button>
        </div>
      </div>
    </div>
  );
}
